import { Component, Suspense, useEffect, useMemo, useRef, type ReactNode } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { Text3D, Center, Float } from '@react-three/drei'
import * as THREE from 'three'

interface BrandText3DFallbackBoundaryProps {
  children: ReactNode
  fallback: ReactNode
}

interface BrandText3DFallbackBoundaryState {
  hasError: boolean
}

class BrandText3DFallbackBoundary extends Component<
  BrandText3DFallbackBoundaryProps,
  BrandText3DFallbackBoundaryState
> {
  state: BrandText3DFallbackBoundaryState = { hasError: false }

  static getDerivedStateFromError(): BrandText3DFallbackBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error) {
    console.warn('[BrandText3D] WebGL render failed, using static fallback:', error.message)
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback
    }

    return this.props.children
  }
}

/* ── Icy gradient shader material with bounding-box-normalized gradient ── */
function useIcyGradientMaterial(colorA: string, colorB: string, colorC: string) {
  return useMemo(() => {
    return new THREE.ShaderMaterial({
      side: THREE.DoubleSide,
      uniforms: {
        uTime: { value: 0 },
        uColorA: { value: new THREE.Color(colorA) },
        uColorB: { value: new THREE.Color(colorB) },
        uColorC: { value: new THREE.Color(colorC) },
        uBoundsMin: { value: 0.0 },
        uBoundsMax: { value: 1.0 },
      },
      vertexShader: `
        varying vec3 vPosition;
        varying vec3 vNormal;
        varying vec3 vWorldPosition;

        void main() {
          vPosition = position;
          vNormal = normalize(normalMatrix * normal);
          vec4 worldPos = modelMatrix * vec4(position, 1.0);
          vWorldPosition = worldPos.xyz;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float uTime;
        uniform vec3 uColorA;
        uniform vec3 uColorB;
        uniform vec3 uColorC;
        uniform float uBoundsMin;
        uniform float uBoundsMax;

        varying vec3 vPosition;
        varying vec3 vNormal;
        varying vec3 vWorldPosition;

        void main() {
          // Normalize position across full text bounding box (0 = left edge, 1 = right edge)
          float gradientPos = clamp((vPosition.x - uBoundsMin) / (uBoundsMax - uBoundsMin), 0.0, 1.0);

          // Three-stop gradient: colorA(0.0) → colorB(0.5) → colorC(1.0)
          // For PHYRE: P=blue(0.0), H=blue-purple(0.25), Y=purple(0.5), R=purple-magenta(0.75), E=magenta(1.0)
          vec3 baseColor;
          if (gradientPos < 0.5) {
            baseColor = mix(uColorA, uColorB, gradientPos * 2.0);
          } else {
            baseColor = mix(uColorB, uColorC, (gradientPos - 0.5) * 2.0);
          }

          // View direction for fresnel & specular
          vec3 viewDir = normalize(cameraPosition - vWorldPosition);
          float NdotV = max(dot(vNormal, viewDir), 0.0);

          // Diamond base — deep tinted body with high clarity
          vec3 color = baseColor * 0.6;

          // Strong fresnel — diamonds reflect more at edges (high IOR ~2.42)
          float fresnel = pow(1.0 - NdotV, 4.0);
          color += mix(baseColor * 1.5, vec3(1.0), 0.6) * fresnel * 0.8;

          // Multiple light sources for faceted look
          vec3 lights[3];
          lights[0] = normalize(vec3(1.0, 1.0, 0.5));
          lights[1] = normalize(vec3(-0.5, 0.8, 1.0));
          lights[2] = normalize(vec3(0.0, -0.5, 1.0));

          for (int i = 0; i < 3; i++) {
            // Diffuse — subtle, diamond is mostly specular
            float NdotL = max(dot(vNormal, lights[i]), 0.0);
            color += baseColor * NdotL * 0.15;

            // Sharp specular — high shininess mimics polished facets
            vec3 halfDir = normalize(lights[i] + viewDir);
            float spec = pow(max(dot(vNormal, halfDir), 0.0), 128.0);
            color += vec3(1.0) * spec * 0.9;

            // Secondary broader specular for internal glow
            float spec2 = pow(max(dot(vNormal, halfDir), 0.0), 32.0);
            color += baseColor * spec2 * 0.3;
          }

          // Rainbow dispersion — chromatic shift at glancing angles
          float dispersion = pow(1.0 - NdotV, 3.0);
          vec3 rainbow = vec3(
            sin(vPosition.x * 8.0 + uTime * 0.5) * 0.5 + 0.5,
            sin(vPosition.x * 8.0 + uTime * 0.5 + 2.094) * 0.5 + 0.5,
            sin(vPosition.x * 8.0 + uTime * 0.5 + 4.189) * 0.5 + 0.5
          );
          color += rainbow * dispersion * 0.15;

          gl_FragColor = vec4(color, 1.0);
        }
      `,
    })
  }, [colorA, colorB, colorC])
}

/* ── Inner 3D text scene ── */
function TextMesh({
  text,
  colorA,
  colorB,
  colorC,
  autoRotate,
  depth,
}: {
  text: string
  colorA: string
  colorB: string
  colorC: string
  autoRotate: boolean
  depth: number
}) {
  const groupRef = useRef<THREE.Group>(null)
  const textRef = useRef<THREE.Mesh>(null)
  const materialRef = useRef<THREE.ShaderMaterial | null>(null)
  const gradientMat = useIcyGradientMaterial(colorA, colorB, colorC)

  useEffect(() => {
    materialRef.current = gradientMat
  }, [gradientMat])

  const boundsSet = useRef(false)

  // Animate + detect bounding box once geometry is ready (font loads async)
  useFrame((state, delta) => {
    if (groupRef.current && autoRotate) {
      groupRef.current.rotation.y += delta * 0.3
    }
    if (materialRef.current) {
      materialRef.current.uniforms.uTime.value = state.clock.elapsedTime
    }
    // Set bounding box bounds once the text geometry has loaded
    if (!boundsSet.current && textRef.current && textRef.current.geometry) {
      const geo = textRef.current.geometry as THREE.BufferGeometry
      geo.computeBoundingBox()
      if (geo.boundingBox && geo.boundingBox.max.x > geo.boundingBox.min.x) {
        // Shader uniform access — not a React hook call
        // eslint-disable-next-line react-hooks/immutability -- Three.js shader uniform mutation (not a React hook value)
        gradientMat.uniforms.uBoundsMin.value = geo.boundingBox.min.x
        gradientMat.uniforms.uBoundsMax.value = geo.boundingBox.max.x
        boundsSet.current = true
      }
    }
  })

  return (
    <Float speed={1.5} rotationIntensity={0.15} floatIntensity={0.3}>
      <group ref={groupRef}>
        <Center>
          <Text3D
            ref={textRef}
            font="/assets/fonts/helvetiker_bold.typeface.json"
            size={1}
            height={depth}
            bevelEnabled
            bevelThickness={0.03}
            bevelSize={0.02}
            bevelOffset={0}
            bevelSegments={5}
            curveSegments={12}
            letterSpacing={0.06}
          >
            {text}
            <primitive object={gradientMat} attach="material" />
          </Text3D>
        </Center>
      </group>
    </Float>
  )
}

/* ── Props ── */
interface BrandText3DProps {
  text: string
  className?: string
  autoRotate?: boolean
  /** Extrusion depth */
  depth?: number
  /** Three-stop gradient colors: primary → mid → accent */
  colorA?: string
  colorB?: string
  colorC?: string
  /** Primary light color */
  lightColor?: string
  /** Accent light color */
  accentColor?: string
}

/* ── Public component ── */
export default function BrandText3D({
  text,
  className = '',
  autoRotate = false,
  depth = 0.3,
  colorA = '#60A5FA',
  colorB = '#A855F7',
  colorC = '#EC4899',
  lightColor = '#60A5FA',
  accentColor = '#EC4899',
}: BrandText3DProps) {
  return (
    <div className={`w-full h-full ${className}`}>
      <BrandText3DFallbackBoundary
        fallback={
          <BrandText3DStaticFallback text={text} colorA={colorA} colorB={colorB} colorC={colorC} />
        }
      >
        <Canvas
          camera={{ position: [0, 0, 6], fov: 50 }}
          gl={{
            antialias: true,
            alpha: true,
            powerPreference: 'high-performance',
            premultipliedAlpha: false,
          }}
          style={{ background: 'transparent' }}
          dpr={[1, 2]}
          onCreated={({ gl, scene }) => {
            gl.setClearColor(0x000000, 0)
            scene.background = null
          }}
        >
          <Suspense fallback={null}>
            {/* Lighting — themed to brand colors */}
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 5, 5]} intensity={1.2} color={lightColor} />
            <directionalLight position={[-5, 3, -5]} intensity={0.8} color={accentColor} />
            <pointLight position={[0, -3, 2]} intensity={0.6} color={colorB} />
            <pointLight position={[0, 4, -2]} intensity={0.4} color={lightColor} />

            <TextMesh
              text={text}
              colorA={colorA}
              colorB={colorB}
              colorC={colorC}
              autoRotate={autoRotate}
              depth={depth}
            />
          </Suspense>
        </Canvas>
      </BrandText3DFallbackBoundary>
    </div>
  )
}

function BrandText3DStaticFallback({
  text,
  colorA,
  colorB,
  colorC,
}: {
  text: string
  colorA: string
  colorB: string
  colorC: string
}) {
  return (
    <div
      className="flex h-full w-full items-center justify-center"
      data-testid="brand-text-3d-fallback"
      aria-label={`${text} brand mark`}
    >
      <span
        className="font-serif font-semibold leading-none tracking-[0.06em] text-transparent"
        style={{
          backgroundImage: `linear-gradient(115deg, ${colorA}, ${colorB} 52%, ${colorC})`,
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          fontSize: 'clamp(1.5rem, 10vw, 2.8rem)',
          textShadow: '0 18px 50px rgba(96, 165, 250, 0.22)',
        }}
      >
        {text}
      </span>
    </div>
  )
}
